import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownToLine,
  Ban,
  CheckCircle2,
  ChevronsDown,
  FlaskConical,
  Hand,
  Loader2,
  RotateCcw,
  SquareTerminal,
  XCircle,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { innerSurface, toneWash } from '@/components/surface/tones';
import { Disclosure, Panel, Pill, SectionHeader, Skeleton } from '@/components/surface/ui';
import { Button } from '@/components/ui/button';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { RUNS_KEY, automationApi, runKey } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { shortDateTime } from '@/modules/backups/format';
import { humanSize } from '@/modules/backups/format';
import { LogBuffer, type LogLine, type MarkerKind } from '@/modules/automation/log';
import { countsSentence, formatDuration, targetTone } from '@/modules/automation/status';
import { PageHeader, RunStatusPill, TargetStatusGlyph } from '@/modules/automation/ui';
import type { RunDetail, RunTarget } from '@/types/api';

/** How many lines the viewer draws. The whole log is always one download away. */
const MAX_VISIBLE_LINES = 4000;

/**
 * One run, live: every server's state, the canary waiting for a person, and the output of each server
 * a second after the server printed it.
 *
 * ## Polling, deliberately
 *
 * A second is fast enough to watch a package manager work, and polling needs nothing between the
 * browser and the database that could fail on its own. The run is re-read every second while it is
 * unfinished; the log asks only for what it has not seen, by sequence number, and stops when its
 * server's work is over.
 */
export function RunDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { permitsChanges } = useSubscriptionAccess();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const detail = useQuery({
    queryKey: runKey(id),
    queryFn: () => automationApi.getRun(id),
    refetchInterval: (query) => {
      const run = query.state.data?.run;
      if (run === undefined) return 1000;
      if (run.is_finished) return false;
      return run.status === 'awaiting_confirmation' ? 3000 : 1000;
    },
  });

  const data = detail.data;
  const run = data?.run;
  const targets = useMemo(() => data?.targets ?? [], [data]);

  // Follows the server being worked on until the person picks one themselves.
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if (targets.length === 0) return;
    if (pinned && targets.some((t) => t.id === selectedId)) return;
    const working = targets.find((t) => t.status === 'running' || t.status === 'checking');
    const failed = targets.find((t) => t.status === 'failed' || t.status === 'blocked');
    const next = working ?? (run?.is_finished ? failed : undefined) ?? targets.find((t) => t.id === selectedId) ?? targets[0];
    if (next !== undefined && next.id !== selectedId) setSelectedId(next.id);
  }, [targets, pinned, selectedId, run?.is_finished]);

  const refresh = (next?: RunDetail) => {
    if (next !== undefined) queryClient.setQueryData(runKey(id), next);
    void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
  };

  const confirm = useMutation({ mutationFn: () => automationApi.confirmRun(id), onSuccess: refresh });
  const cancel = useMutation({
    mutationFn: () => automationApi.cancelRun(id),
    onSuccess: (next) => {
      setConfirmCancel(false);
      refresh(next);
    },
  });
  const retry = useMutation({
    mutationFn: () => automationApi.retryRun(id),
    onSuccess: (next) => {
      refresh();
      navigate(`/automation/runs/${next.id}`);
    },
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28" />
        <Skeleton className="h-24" />
        <Skeleton className="h-[28rem]" />
      </div>
    );
  }

  if (detail.isError || data === undefined || run === undefined) {
    return (
      <div className="space-y-4">
        <PageHeader title="Run not found" back={{ to: '/automation/runs', label: 'Runs' }} />
        <p className="text-sm text-muted-foreground">{detail.isError ? getErrorMessage(detail.error) : 'This run does not exist.'}</p>
      </div>
    );
  }

  const selected = targets.find((t) => t.id === selectedId) ?? null;
  const retryable = targets.some((t) => ['blocked', 'failed', 'cancelled', 'skipped'].includes(t.status));
  const canary = targets.find((t) => t.is_canary);
  const waiting = targets.filter((t) => t.status === 'ready').length;
  const actionError = confirm.error ?? retry.error;

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ to: '/automation/runs', label: 'Runs' }}
        eyebrow={
          <>
            <RunStatusPill status={run.status} label={run.status_label} />
            <Pill tone="muted">{run.kind_label}</Pill>
            {run.mode === 'dry_run' && (
              <Pill tone="primary" icon={FlaskConical}>
                Dry run: nothing is changed
              </Pill>
            )}
            {run.subject_version !== '' && run.kind !== 'command' && <Pill tone="muted">Version {run.subject_version}</Pill>}
          </>
        }
        title={run.title}
        description={run.summary !== '' ? run.summary : 'Waiting for the runner to pick this up.'}
        actions={
          <>
            {run.kind === 'command' && (
              <Link to={`/automation/commands?from=${run.id}`} className="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium text-foreground hover:bg-muted/50">
                <SquareTerminal className="size-4" aria-hidden /> Run again
              </Link>
            )}
            {run.is_finished && retryable && (
              <Button variant="outline" size="sm" onClick={() => retry.mutate()} disabled={!permitsChanges || retry.isPending}>
                {retry.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                Retry servers that did not succeed
              </Button>
            )}
            {!run.is_finished && (
              <Button variant="outline" size="sm" onClick={() => setConfirmCancel(true)} disabled={run.cancel_requested || cancel.isPending}>
                {run.cancel_requested ? <Loader2 className="animate-spin" /> : <Ban />}
                {run.cancel_requested ? 'Cancelling…' : 'Cancel run'}
              </Button>
            )}
          </>
        }
      />

      {actionError !== null && (
        <p className="rounded-lg bg-danger/[0.06] px-4 py-2.5 text-sm dark:bg-danger/[0.08]" role="alert">
          {getErrorMessage(actionError)}
        </p>
      )}

      {run.status === 'awaiting_confirmation' && (
        <div className={cn('flex flex-wrap items-center gap-4 rounded-xl px-5 py-4 ring-1 ring-warning/30', toneWash.warning)}>
          <Hand className="size-5 shrink-0 text-warning" aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-medium">
              {canary !== undefined ? `The canary server, ${canary.server_name}, succeeded.` : 'The first server succeeded.'}
            </p>
            <p className="text-muted-foreground">
              Check its output below. Nothing else starts until you continue. {waiting} server{waiting === 1 ? '' : 's'} waiting.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmCancel(true)} disabled={cancel.isPending}>
              Stop here
            </Button>
            <Button className="!shadow-none" onClick={() => confirm.mutate()} disabled={!permitsChanges || confirm.isPending}>
              {confirm.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}
              Continue on {waiting} server{waiting === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      <RunFacts detail={data} />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
        <Panel>
          <SectionHeader title="Servers" description={countsSentence(run)} />
          <ul className="max-h-[36rem] divide-y divide-slate-900/[0.06] overflow-y-auto pb-2 dark:divide-white/[0.05]" aria-label="Servers in this run">
            {targets.map((target) => (
              <li key={target.id}>
                <button
                  type="button"
                  onClick={() => {
                    setPinned(true);
                    setSelectedId(target.id);
                  }}
                  aria-current={target.id === selectedId}
                  className={cn(
                    'flex w-full items-start gap-3 px-5 py-2.5 text-left transition-colors hover:bg-foreground/[0.03]',
                    target.id === selectedId && 'bg-primary/[0.06] dark:bg-primary/[0.1]',
                  )}
                >
                  <TargetStatusGlyph status={target.status} label={target.status_label} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-[13px] font-medium">{target.server_name}</span>
                      {target.is_canary && <Pill tone="warning">Canary</Pill>}
                    </span>
                    <span className={cn('block truncate text-xs', target.status === 'failed' || target.status === 'blocked' ? 'text-danger' : 'text-muted-foreground')}>
                      {target.summary !== '' ? target.summary : target.status_label}
                    </span>
                  </span>
                  <span className="shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
                    {target.duration_seconds !== null && formatDuration(target.duration_seconds)}
                    {target.changed === true && <span className="block text-primary">changed</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        {selected !== null && <TargetPanel key={selected.id} runId={run.id} target={selected} />}
      </div>

      {(data.parent !== null || data.retries.length > 0) && (
        <p className="text-sm text-muted-foreground">
          {data.parent !== null && (
            <>
              A retry of <Link to={`/automation/runs/${data.parent.id}`} className="text-primary hover:underline">{data.parent.title}</Link>.{' '}
            </>
          )}
          {data.retries.length > 0 && (
            <>
              Retried as{' '}
              {data.retries.map((retried, index) => (
                <span key={retried.id}>
                  {index > 0 && ', '}
                  <Link to={`/automation/runs/${retried.id}`} className="text-primary hover:underline">
                    {retried.title}
                  </Link>
                </span>
              ))}
              .
            </>
          )}
        </p>
      )}

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel this run?"
        description="Servers not started yet will not be touched. Servers being worked on now have their current step stopped on the server; what that step had already done is not undone."
        confirmLabel="Cancel run"
        busy={cancel.isPending}
        error={cancel.error}
        onConfirm={() => cancel.mutate()}
      />
    </div>
  );
}

function RunFacts({ detail }: { detail: RunDetail }) {
  const { run } = detail;
  const values = run.parameters.values ?? {};
  const steps = run.parameters.steps ?? [];

  return (
    <Panel>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 px-5 py-4 text-[13px] md:grid-cols-3 xl:grid-cols-6">
        <Fact label="Servers" value={run.targets_label} />
        <Fact label="Started by" value={run.requested_by || 'Not recorded'} />
        <Fact label="Created" value={shortDateTime(run.created_at)} />
        <Fact label="Duration" value={formatDuration(run.duration_seconds)} />
        <Fact
          label="Strategy"
          value={`${run.strategy.canary ? 'Canary first, ' : ''}${run.strategy.batch_size} at a time`}
        />
        <Fact label="Stops after" value={run.strategy.max_failures === 0 ? 'First failure' : `${run.strategy.max_failures + 1} failures`} />
      </dl>

      {(run.kind === 'command' || Object.keys(values).length > 0 || steps.length > 0 || run.confirmed_by !== null) && (
        <Disclosure title="What was asked" meta={run.kind === 'command' ? run.run_as_label : undefined}>
          <div className="space-y-3 px-5 text-[13px]">
            {run.kind === 'command' && (
              <pre className={cn(innerSurface, 'overflow-x-auto whitespace-pre-wrap break-words p-3 font-mono text-[12px] leading-5')}>{run.command}</pre>
            )}
            {Object.keys(values).length > 0 && (
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                {Object.entries(values).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <dt className="text-muted-foreground">{key}</dt>
                    <dd className="font-mono text-[12px]">{Array.isArray(value) ? value.join(', ') || 'None' : String(value)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {steps.length > 0 && (
              <ol className="list-decimal space-y-1 pl-5">
                {steps.map((step, index) => (
                  <li key={`${step.playbook}-${index}`}>
                    {step.label}{' '}
                    <span className="text-xs text-muted-foreground">
                      {Object.entries(step.values)
                        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
                        .join(' · ')}
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {run.confirmed_by !== null && <p className="text-muted-foreground">Confirmed past the canary by {run.confirmed_by}.</p>}
          </div>
        </Disclosure>
      )}
    </Panel>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium" title={value}>
        {value}
      </dd>
    </div>
  );
}

function TargetPanel({ runId, target }: { runId: string; target: RunTarget }) {
  const { lines, done, error } = useTargetLog(runId, target.id);
  const [follow, setFollow] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  const visible = lines.length > MAX_VISIBLE_LINES ? lines.slice(-MAX_VISIBLE_LINES) : lines;

  useLayoutEffect(() => {
    const element = scroller.current;
    if (element !== null && follow) {
      element.scrollTop = element.scrollHeight;
    }
  }, [visible, follow]);

  const onScroll = () => {
    const element = scroller.current;
    if (element === null) return;
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
    setFollow(atBottom);
  };

  const failedChecks = target.checks.filter((check) => !check.ok);

  return (
    <Panel className="min-w-0">
      <SectionHeader
        title={target.server_name}
        description={`${target.server_endpoint}${target.exit_code !== null ? ` · exit code ${target.exit_code}` : ''}${target.log_bytes > 0 ? ` · ${humanSize(target.log_bytes)} of output` : ''}`}
        badge={<Pill tone={targetTone[target.status]}>{target.status_label}</Pill>}
        actions={
          <Button
            variant="ghost"
            size="sm"
            disabled={downloading || target.log_bytes === 0}
            onClick={() => {
              setDownloading(true);
              void automationApi.downloadTargetLog(runId, target.id).finally(() => setDownloading(false));
            }}
          >
            {downloading ? <Loader2 className="animate-spin" /> : <ArrowDownToLine />}
            Download log
          </Button>
        }
      />

      <div className="space-y-4 px-5 pb-5">
        {target.checks.length > 0 && (
          <ul className={cn('grid grid-cols-1 gap-1.5 text-[13px]', failedChecks.length === 0 && 'sm:grid-cols-2')} aria-label="Preflight checks">
            {target.checks.map((check) => (
              <li key={check.key} className="flex items-start gap-2">
                {check.ok ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-label="Passed" /> : <XCircle className="mt-0.5 size-4 shrink-0 text-danger" aria-label="Failed" />}
                <span className="min-w-0">
                  <span className="font-medium">{check.label}:</span> <span className={check.ok ? 'text-muted-foreground' : ''}>{check.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        {target.steps.length > 1 && (
          <ol className="flex flex-wrap gap-1.5 text-xs" aria-label="Steps">
            {target.steps.map((step, index) => (
              <li
                key={`${step.key}-${index}`}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-1',
                  step.status === 'succeeded' && 'bg-success/[0.1] text-success',
                  step.status === 'failed' && 'bg-danger/[0.1] text-danger',
                  step.status === 'running' && 'bg-primary/[0.1] text-primary',
                  ['pending', 'skipped', 'cancelled'].includes(step.status) && 'bg-slate-900/[0.05] text-muted-foreground dark:bg-white/[0.05]',
                )}
                title={step.summary}
              >
                {step.status === 'running' && <Loader2 className="size-3 animate-spin" aria-hidden />}
                {index + 1}. {step.label}
                {step.attempts > 1 && <span className="opacity-70"> ×{step.attempts}</span>}
              </li>
            ))}
          </ol>
        )}

        <div className="relative">
          <div
            ref={scroller}
            onScroll={onScroll}
            className="h-[30rem] overflow-auto rounded-lg bg-slate-950 px-4 py-3 font-mono text-[12px] leading-5 text-slate-200 ring-1 ring-slate-900/[0.2] dark:bg-black/60 dark:ring-white/[0.06]"
            role="log"
            aria-live="off"
            aria-label={`Output from ${target.server_name}`}
          >
            {lines.length > MAX_VISIBLE_LINES && (
              <p className="mb-2 text-slate-400">Earlier lines are hidden here. Download the log for all of it.</p>
            )}
            {visible.length === 0 && !done && (
              <p className="text-slate-400">{target.status === 'pending' || target.status === 'queued' || target.status === 'ready' ? 'Waiting to start…' : 'Waiting for output…'}</p>
            )}
            {visible.length === 0 && done && <p className="text-slate-400">This server printed nothing.</p>}
            {visible.map((line) => (
              <LogRow key={line.id} line={line} />
            ))}
            {!done && visible.length > 0 && (target.status === 'running' || target.status === 'checking') && (
              <span className="inline-block h-3.5 w-1.5 animate-pulse bg-slate-400 align-middle" aria-hidden />
            )}
            {target.log_truncated && <p className="mt-2 text-amber-300">Output past the storage limit was not kept. The step itself was left to finish.</p>}
          </div>
          {!follow && (
            <button
              type="button"
              onClick={() => setFollow(true)}
              className="absolute bottom-3 right-4 inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground shadow"
            >
              <ChevronsDown className="size-3.5" aria-hidden /> Follow output
            </button>
          )}
        </div>
        {error !== null && <p className="text-xs text-danger">The log could not be read just now ({error}). Trying again.</p>}
      </div>
    </Panel>
  );
}

const MARKER_STYLE: Record<MarkerKind, { label: string; className: string }> = {
  ok: { label: 'OK', className: 'bg-emerald-500/15 text-emerald-300' },
  changed: { label: 'CHANGED', className: 'bg-sky-500/20 text-sky-300' },
  would: { label: 'WOULD', className: 'bg-violet-500/20 text-violet-300' },
  note: { label: 'NOTE', className: 'bg-amber-500/15 text-amber-300' },
  error: { label: 'ERROR', className: 'bg-rose-500/20 text-rose-300' },
  result: { label: 'RESULT', className: 'bg-slate-500/25 text-slate-100' },
};

function LogRow({ line }: { line: LogLine }) {
  if (line.stream === 'sys') {
    return <div className="whitespace-pre-wrap break-words text-sky-300/90">» {line.text}</div>;
  }

  if (line.marker !== null) {
    const style = MARKER_STYLE[line.marker];

    return (
      <div className="whitespace-pre-wrap break-words">
        <span className={cn('mr-2 inline-block rounded px-1 text-[10px] font-semibold leading-4', style.className)}>{style.label}</span>
        <span className={line.marker === 'error' ? 'text-rose-200' : 'text-slate-100'}>{line.text}</span>
      </div>
    );
  }

  return <div className="whitespace-pre-wrap break-words">{line.text === '' ? ' ' : line.text}</div>;
}

/**
 * What one server printed, read a second at a time from the last sequence number already held.
 * Stops once the server's work is over and everything has been read.
 */
function useTargetLog(runId: string, targetId: string) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let after = 0;
    const buffer = new LogBuffer();

    setLines([]);
    setDone(false);
    setError(null);

    const poll = async () => {
      try {
        const page = await automationApi.targetLog(runId, targetId, after);

        if (cancelled) return;

        setError(null);

        if (page.chunks.length > 0) {
          buffer.append(page.chunks);
          after = page.last_seq;
          setLines(buffer.view());
        }

        if (page.done) {
          setDone(true);
          return;
        }

        // A full page means more is already waiting: read it at once rather than a second later.
        timer = window.setTimeout(() => void poll(), page.chunks.length >= 500 ? 0 : 1000);
      } catch (caught) {
        if (cancelled) return;
        setError(getErrorMessage(caught));
        timer = window.setTimeout(() => void poll(), 3000);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [runId, targetId]);

  return { lines, done, error };
}
